import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, type Domain } from '../api';
import { DnsRecords } from '../components/DnsRecords';
import { RefreshIcon, SendIcon, TrashIcon } from '../components/icons';
import {
  Dialog,
  ErrorNote,
  formatDateTime,
  PageHeader,
  Status,
  timeAgo,
  useToast,
} from '../components/ui';
import { useLoad } from '../hooks';
import { useSession } from '../session';

export function DomainDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { team } = useSession();
  const canEdit = team.role !== 'member';
  const {
    data: domain,
    error,
    loading,
    setData,
  } = useLoad(() => api<Domain>('GET', `/domains/${encodeURIComponent(id)}`), [id]);
  const [checking, setChecking] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const toast = useToast();

  if (loading && !domain) return <div className="skeleton" style={{ height: 28, width: 260 }} />;
  if (error || !domain) return <ErrorNote error={error ?? 'Domain not found.'} />;

  async function check() {
    setChecking(true);
    try {
      const updated = await api<Domain>('POST', `/domains/${encodeURIComponent(id)}/verify`);
      setData(updated);
      toast.show(
        updated.status === 'verified'
          ? 'Domain verified'
          : 'Checked: some records are still missing',
      );
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Check failed');
    } finally {
      setChecking(false);
    }
  }

  const verified = domain.status === 'verified';

  return (
    <>
      <PageHeader
        crumbs={
          <>
            <Link to="/domains">Domains</Link>
            <span>/</span>
            <span>{domain.name}</span>
          </>
        }
        title={domain.name}
        description={
          <>
            <Status status={domain.status} />
            <span style={{ marginLeft: 10 }}>
              {verified
                ? `Verified ${formatDateTime(domain.verifiedAt)}`
                : `Last checked ${timeAgo(domain.lastCheckedAt)} · we check every 10 minutes for 72 hours`}
            </span>
          </>
        }
        actions={
          <>
            {verified ? (
              <Link
                to={`/emails/new?domain=${encodeURIComponent(domain.name)}`}
                className="btn btn-primary"
              >
                <SendIcon />
                Send test email
              </Link>
            ) : null}
            {canEdit ? (
              <>
                {verified ? null : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={checking}
                    onClick={() => void check()}
                  >
                    <RefreshIcon />
                    {checking ? 'Checking…' : 'Check now'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setConfirmDelete(true)}
                >
                  <TrashIcon />
                  Delete
                </button>
              </>
            ) : null}
          </>
        }
      />

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="card-head">
          <div>
            <h2>DNS records</h2>
            <p>
              {verified
                ? 'Keep these records in place; removing them stops sending.'
                : 'Add these at your DNS provider.'}
            </p>
          </div>
        </div>
        <div style={{ padding: '12px 12px 16px' }}>
          <DnsRecords domain={domain.name} records={domain.records} />
        </div>
      </div>

      {confirmDelete ? (
        <Dialog
          title={`Delete ${domain.name}?`}
          description="Emails can no longer be sent from this domain. You can add it again later, but you'll need to verify it again."
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmDelete(false)}
              >
                Keep it
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  try {
                    await api('DELETE', `/domains/${encodeURIComponent(domain.id)}`);
                    navigate('/domains');
                  } catch (err) {
                    setConfirmDelete(false);
                    toast.show(err instanceof Error ? err.message : 'Could not delete');
                  }
                }}
              >
                Delete domain
              </button>
            </>
          }
        />
      ) : null}
      {toast.node}
    </>
  );
}
